import { observer } from "mobx-react-lite";
import { Flex } from "@chakra-ui/react";

import { Lang } from "@noli/core";
import { apiSchema } from "@noli/svg";
import {
  INoliTextFrameUIInfoResponse,
  langStore,
  translate,
  useSelectingUIInfo,
} from "@noli/business";

import type { ISubPanel } from "@/components/SubPanel";
import { ReactComponent as BoldIcon } from "@/assets/icons/text/bold.svg";
import { ReactComponent as ItalicsIcon } from "@/assets/icons/text/italics.svg";
import { ReactComponent as DeleteLineIcon } from "@/assets/icons/text/delete-line.svg";
import { ReactComponent as UnderlineIcon } from "@/assets/icons/text/underline.svg";
import { ReactComponent as VerticalIcon } from "@/assets/icons/text/vertical.svg";
import SubPanelWithScrollBar from "@/components/SubPanel/WithScrollBar";
import Property from "@/components/Property";
import SchemaProperty from "@/components/Property/SchemaProperty";
import SubEditorPanel from "@/components/SubPanel/SubEditor";
import { NoliFrameLayersPanel } from "./common";
import Icon from "@/components/Icon";
import { themeStore } from "@/stores/theme";
import { Editor_Words } from "@/utils/lang/editor";

type NoliTextFrameEditorProps = ISubPanel & { lang: Lang; res: INoliTextFrameUIInfoResponse };

function NoliTextFrameContentEditor({
  lang,
  res: { displayNode, uiInfo, setContent },
  ...others
}: NoliTextFrameEditorProps) {
  return (
    <SubEditorPanel panelTitle={translate("text-content-title", lang)} {...others}>
      <Property
        identifier={`${displayNode.alias}-content`}
        w="90%"
        h="32px"
        mx="5%"
        mt="6px"
        captionW="0px"
        inputPadding="10px"
        text=""
        placeholder={translate(Editor_Words["noliFrame-placeholder"], lang)}
        _placeholder={{
          color: "#6a6a6a",
        }}
        value={uiInfo.content}
        setValue={setContent({ trace: true })}
      />
    </SubEditorPanel>
  );
}

function NoliTextFrameBasicEditor({
  lang,
  res: {
    displayNode,
    uiInfo,
    setSrc,
    setFontSize,
    setLineHeight,
    setLetterSpacing,
    setVertical,
    setAlignX,
    setAlignY,
    setBold,
    setItalics,
    setStrikeThrough,
    setUnderline,
  },
  ...others
}: NoliTextFrameEditorProps) {
  const {
    src,
    fontSize,
    lineHeight,
    letterSpacing,
    vertical,
    alignX,
    alignY,
    bold,
    italics,
    strikethrough,
    underline,
  } = uiInfo;
  const {
    src: srcSchema,
    fontSize: fontSizeSchema,
    lineHeight: lineHeightSchema,
    letterSpacing: letterSpacingSchema,
    base: baseSchema,
    compose: composeSchema,
    glyph: glyphSchema,
  } = apiSchema.noliTextFrame.options;
  const verticalSchema = baseSchema.allPacks.normal.vertical;
  const { alignX: alignXSchema, alignY: alignYSchema } = composeSchema.allPacks.align;
  const {
    bold: boldSchema,
    italics: italicsSchema,
    strikethrough: strikethroughSchema,
    underline: underlineSchema,
  } = glyphSchema.allPacks.basic;
  const { iconButtonColor } = themeStore.styles;

  const alias = displayNode.alias;
  const inputProps = { noSlider: true, w: "40%", ml: "16px", captionW: "48px" };
  const buttonProps = { mt: "8px", fontSize: 20, color: iconButtonColor };
  const selectProps = { w: "40%", px: "8px", mt: "8px", ml: "16px" };

  return (
    <SubEditorPanel panelTitle={translate("text-settings-title", lang)} pb="16px" {...others}>
      <Flex w="100%" wrap="wrap">
        <SchemaProperty
          identifier={`${alias}-src`}
          {...inputProps}
          apiKey="src"
          apiOption={srcSchema}
          value={src}
          setValue={setSrc}
          dictionary={{ src: "font-src" }}
          lang={lang}
        />
        <SchemaProperty
          identifier={`${alias}-fontSize`}
          {...inputProps}
          apiKey="fontSize"
          apiOption={fontSizeSchema}
          value={fontSize}
          setValue={setFontSize}
          lang={lang}
        />
        <SchemaProperty
          identifier={`${alias}-lineHeight`}
          {...inputProps}
          apiKey="lineHeight"
          apiOption={lineHeightSchema}
          value={lineHeight}
          setValue={setLineHeight}
          dictionary={{ lineHeight: "lineH" }}
          lang={lang}
        />
        <SchemaProperty
          identifier={`${alias}-letterSpacing`}
          {...inputProps}
          apiKey="letterSpacing"
          apiOption={letterSpacingSchema}
          value={letterSpacing}
          setValue={setLetterSpacing}
          dictionary={{ letterSpacing: "font-gap" }}
          lang={lang}
        />
        <SchemaProperty
          identifier={`${alias}-bold`}
          {...buttonProps}
          ml={inputProps.ml}
          apiKey="bold"
          apiOption={boldSchema}
          value={bold}
          setValue={setBold}
          booleanIcon={<Icon svg={BoldIcon} fillbyCurrentColor />}
          lang={lang}
        />
        <SchemaProperty
          identifier={`${alias}-italics`}
          {...buttonProps}
          apiKey="italics"
          apiOption={italicsSchema}
          value={italics}
          setValue={setItalics}
          booleanIcon={<Icon svg={ItalicsIcon} fillbyCurrentColor />}
          lang={lang}
        />
        <SchemaProperty
          identifier={`${alias}-strikethrough`}
          {...buttonProps}
          apiKey="strikethrough"
          apiOption={strikethroughSchema}
          value={strikethrough}
          setValue={setStrikeThrough}
          booleanIcon={<Icon svg={DeleteLineIcon} fillbyCurrentColor />}
          lang={lang}
        />
        <SchemaProperty
          identifier={`${alias}-underline`}
          {...buttonProps}
          apiKey="underline"
          apiOption={underlineSchema}
          value={underline}
          setValue={setUnderline}
          booleanIcon={<Icon svg={UnderlineIcon} fillbyCurrentColor />}
          lang={lang}
        />
        <SchemaProperty
          identifier={`${alias}-vertical`}
          {...buttonProps}
          apiKey="vertical"
          apiOption={verticalSchema}
          value={vertical}
          setValue={setVertical}
          booleanIcon={<Icon svg={VerticalIcon} fillbyCurrentColor />}
          lang={lang}
        />
        <SchemaProperty
          identifier={`${alias}-alignX`}
          {...selectProps}
          apiKey="alignX"
          apiOption={alignXSchema}
          value={alignX}
          setValue={setAlignX}
          lang={lang}
        />
        <SchemaProperty
          identifier={`${alias}-alignY`}
          {...selectProps}
          apiKey="alignY"
          apiOption={alignYSchema}
          value={alignY}
          setValue={setAlignY}
          lang={lang}
        />
      </Flex>
    </SubEditorPanel>
  );
}

function NoliTextFrameEditor() {
  const lang = langStore.tgt;
  const res = useSelectingUIInfo();
  if (!res) {
    return null;
  }
  if (res.type !== "noliTextFrame") {
    return null;
  }
  return (
    <SubPanelWithScrollBar w="100%" h="100%">
      <NoliTextFrameContentEditor lang={lang} res={res} h="100px" />
      <NoliTextFrameBasicEditor lang={lang} res={res} />
      <NoliFrameLayersPanel lang={lang} res={res} w="100%" flex={1} />
    </SubPanelWithScrollBar>
  );
}

export default observer(NoliTextFrameEditor);
